import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import {
  CoreActionSetUserSignature,
  CoreActionSetUserSignatureSuccess
} from '@bepatient/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import SignaturePad from 'signature_pad';
import { ThemeService } from '../../../services/theme.service';

@Component({
  selector: 'app-signature-modal',
  templateUrl: './signature-modal.component.html',
  styleUrls: ['./signature-modal.component.scss'],
  standalone: false
})
export class SignatureModalComponent implements OnInit {
  private modalCtrl = inject(ModalController);
  private store = inject<Store<any>>(Store);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);

  @Input() color: string;
  @Input() data: any;

  @ViewChild('canvas', { static: true })
  signaturePadElement: ElementRef<HTMLCanvasElement>;

  signaturePad: any;
  canvasWidth: number;
  canvasHeight: number;

  canvasRef: HTMLCanvasElement;
  canvasCtx: any;

  inMemCanvas: HTMLCanvasElement;
  inMemCanvasCtx: any;

  get value(): string {
    if (this.signaturePad) {
      return !this.isCanvasBlank() ? this.signaturePad.toDataURL() : void 0;
    }
    return void 0;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    /**
     * On window resize: Saving previous signature into memory
     * Redrawing signature into canvas again after resizing
     */
    this.inMemCanvas.width = this.canvasRef.width;
    this.inMemCanvasCtx.height = this.canvasRef.height;

    this.inMemCanvasCtx.drawImage(this.canvasRef, 0, 0);
    this.canvasRef.width = window.innerWidth;
    this.canvasCtx.drawImage(this.inMemCanvas, 0, 0);
  }

  ngOnInit(): void {
    this.init();
  }

  init() {
    // Embedding canvas into signature pad
    this.signaturePad = new SignaturePad(
      this.signaturePadElement.nativeElement
    );

    // Clear the pad on init
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.signaturePad.penColor = '#000';
    }

    // Getting canvas from signature element ref
    this.canvasRef = this.signaturePadElement.nativeElement;
    this.canvasCtx = this.canvasRef.getContext('2d');

    // Making in memory canvas
    this.inMemCanvas = document.createElement('canvas');
    this.inMemCanvasCtx = this.inMemCanvas.getContext('2d');

    // Canvas height/width
    this.canvasRef.width = window.innerWidth;
    this.canvasRef.height = 300;

    // Check canvas is empty or not
    this.isCanvasBlank();
  }

  /**
   * Method: Add canvas background text, if canvas is empty
   * */
  addBlankCanvasText(): void {
    const isDarkMode =
      this.themeService.getCurrentThemeColor() === 'dark' ? true : false;
    // Checking canvas context is set or not
    if (this.canvasCtx) {
      this.canvasCtx.font = '500 18px Ubuntu';
      this.canvasCtx.fillStyle = isDarkMode
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(0, 0, 0, 0.3)';
      this.canvasCtx.textAlign = 'center';
      this.canvasCtx.fillText(
        this.translate.instant('SIGNATURE.MODAL.PLACEHOLDER'),
        window.innerWidth / 2,
        this.signaturePadElement.nativeElement.height / 2
      );
    }
  }

  ngAfterViewInit(): void {
    // Adding Blank canvas text
    this.addBlankCanvasText();

    // Check signature begin
    this.checkSignatureBegin();
  }

  /**
   * Method: To check If signature being to start or not
   * If started remove blank canvas text
   */
  checkSignatureBegin(): void {
    this.signaturePad.addEventListener(
      'beginStroke',
      () => {
        this.store.dispatch(new CoreActionSetUserSignature());
        /**
         * Remove the placeholder text by clearing the canvas pixels directly.
         * We must NOT call signaturePad.clear() here: in signature_pad v5 clear()
         * resets the internal _strokePointerId to undefined, which makes every
         * subsequent pointermove fail the pointer-id check so nothing gets drawn.
         */
        this.canvasCtx.clearRect(
          0,
          0,
          this.canvasRef.width,
          this.canvasRef.height
        );
      },
      { once: true }
    );
  }

  dismissModal(): void {
    this.modalCtrl.dismiss();
  }

  /**
   * Method: To check canvas is blank or not
   */
  isCanvasBlank() {
    return this.signaturePad.isEmpty();
  }

  /**
   * Method: To reset canvas
   */
  reset() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.addBlankCanvasText();
      this.checkSignatureBegin();
    }
  }

  /**
   * Method: To validate a signature
   * Store signature data to store for further use
   */
  validateSignature() {
    this.store.dispatch(
      new CoreActionSetUserSignatureSuccess(this.signaturePad.toDataURL())
    );
    this.modalCtrl.dismiss(null, null, 'signature-modal');
  }
}
